<template>
  <ui-modal-layout>
    <template #header>
      <social-page :source-profile="entity.post.source_profile" :date="entity.created_at" />
    </template>

    <section class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ title }}</h3>
      <inline-social-content :entity="entity.ad ?? entity.post" />
    </section>

    <section class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Automation') }}</h3>
      <router-link
        :to="'/automation/' + rule.id + '/overview'"
        class="inline-flex w-fit items-center gap-2 text-sm font-semibold text-primary hover:underline"
        @click="() => $emit('close')"
      >
        <rule-type-badge :action="rule.action" compact />
        {{ rule.name }}
        <arrow-right-icon class="size-4 shrink-0" />
      </router-link>
    </section>

    <section v-if="whyText" class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ whyTitle }}</h3>
      <p class="m-0 text-sm text-muted-foreground">{{ whyText }}</p>
    </section>

    <section v-if="isAiProhibitedItem" class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Reasoning of Infinite∞Ai') }}</h3>
      <p class="m-0 text-sm text-muted-foreground">{{ entity.reason_translation ?? entity.reason }}</p>
    </section>

    <section v-if="whatToDoText" class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('What can you do?') }}</h3>
      <p class="m-0 text-sm text-muted-foreground">{{ whatToDoText }}</p>
    </section>

    <section class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Status') }}</h3>
      <rule-waiting-list-status
        :rule="rule"
        :entity="entity"
        :disabled="$root.isSupportMode"
        in-modal
      />
    </section>
  </ui-modal-layout>
</template>

<script>
import { ArrowRight } from '@lucide/vue';

import { RULE_REVIEW_CODE, RULE_REVIEW_FILTER_DEFINITIONS } from '@/consts.js';
import PostAiProhibitedContent from '@/entities/PostAiProhibitedContent.js';
import Rule from '@/entities/Rule.js';
import RuleActivityManagementQueueItem from '@/entities/RuleActivityManagementQueueItem.js';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import RuleTypeBadge from '@/ui/components/RuleTypeBadge.vue';
import RuleWaitingListStatus from '@/ui/components/RuleWaitingListStatus.vue';
import SocialPage from '@/ui/components/SocialPage.vue';

export default {
  name: 'RuleWaitingListItemModalContent',
  components: {
    ArrowRightIcon: ArrowRight,
    InlineSocialContent,
    RuleTypeBadge,
    RuleWaitingListStatus,
    SocialPage,
  },
  props: {
    rule: {
      type: Rule,
      required: true,
    },
    entity: {
      type: [ PostAiProhibitedContent, RuleActivityManagementQueueItem ],
      required: true,
    },
  },
  computed: {
    isActivityManagementItem() {
      return this.entity instanceof RuleActivityManagementQueueItem;
    },
    isAiProhibitedItem() {
      return this.entity instanceof PostAiProhibitedContent;
    },
    title() {
      if (this.isActivityManagementItem) {
        const action = this.tr('automation_type_' + this.rule.action);

        if (this.entity.isHandled) {
          if (this.entity.isAccepted) {
            return this.tr('The :action activity was approved!', { ':action': action });
          }

          return this.tr('The :action activity was rejected!', { ':action': action });
        }

        return this.tr('The :action activity is waiting for approval!', { ':action': action });
      }
      else if (this.isAiProhibitedItem) {
        return this.tr('Meta Policy Violation Detected');
      }

      return null;
    },
    isAcceptableAfterRejection() {
      return this.isActivityManagementItem
        && this.entity.canBeAcceptedAfterRejection
        && !this.$root.loggedInUser.isRestrictedViewer;
    },
    rejectedReviewCode() {
      return this.entity.handled_by === null
        ? RULE_REVIEW_CODE.REJECTED_AUTOMATICALLY_IN_ACTIVITY_MANAGEMENT
        : RULE_REVIEW_CODE.REJECTED_MANUALLY_IN_ACTIVITY_MANAGEMENT;
    },
    whyTitle() {
      if (this.isActivityManagementItem) {
        return this.tr('Why is this activity waiting for approval?');
      }
      else if (this.isAiProhibitedItem) {
        return this.tr('Why was this post flagged?');
      }

      return null;
    },
    whyText() {
      if (this.isActivityManagementItem && !this.entity.isHandled) {
        const definition = RULE_REVIEW_FILTER_DEFINITIONS.find(item => item.code === RULE_REVIEW_CODE.IN_ACTIVITY_MANAGEMENT_QUEUE);
        const text = definition.whyNot[this.rule.action];

        return text ? this.tr(text) : null;
      }
      else if (this.isAiProhibitedItem) {
        const definition = RULE_REVIEW_FILTER_DEFINITIONS.find(item => item.code === RULE_REVIEW_CODE.PROHIBITED_CONTENT);
        const text = definition.whyNot[this.rule.action];

        return text ? this.tr(text) : null;
      }

      return null;
    },
    whatToDoText() {
      if (this.entity.isHandled && !this.isAcceptableAfterRejection) {
        return null;
      }

      if (this.isActivityManagementItem) {
        const code = this.entity.isHandled
          ? this.rejectedReviewCode
          : RULE_REVIEW_CODE.IN_ACTIVITY_MANAGEMENT_QUEUE;
        const definition = RULE_REVIEW_FILTER_DEFINITIONS.find(item => item.code === code);
        const text = definition.whatToDo[this.rule.action];

        return text ? this.tr(text) : null;
      }
      else if (this.isAiProhibitedItem) {
        const definition = RULE_REVIEW_FILTER_DEFINITIONS.find(item => item.code === RULE_REVIEW_CODE.PROHIBITED_CONTENT);
        const text = definition.whatToDo[this.rule.action];

        return text ? this.tr(text) : null;
      }

      return null;
    },
  },
  watch: {
    '$route.path'() {
      this.$emit('close');
    },
  },
};
</script>
